import type { ReactNode } from 'react'

/**
 * The written answer, shown as headings, paragraphs, bullets and a table.
 *
 * **Built into React elements, never into HTML.** The answer is written by our
 * own code from a benchmark, but part of it is the agent's reasoning about a
 * job description a customer pasted, so some of these characters came from a
 * person. Setting that as HTML would make a pasted job description able to put
 * a link, a script or a form onto this screen. Building elements means the
 * worst a pasted angle bracket can do is look like an angle bracket.
 *
 * It is a small renderer rather than a markdown library because the shape it
 * has to read is one we write ourselves in `match-answer.ts`: headings,
 * bullets, one table, and bold inside a line. A library would bring a parser
 * for everything else as well, and every one of those is another thing that
 * can be made to render something we did not intend.
 */
export function Answer({ text }: { text: string }) {
  return <div className="answer">{blocks(text)}</div>
}

function blocks(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const lines = text.split('\n')
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') {
      i++
      continue
    }

    // A heading.
    const heading = /^(#{2,3})\s+(.*)$/.exec(line)
    if (heading) {
      const Tag = heading[1].length === 2 ? 'h3' : 'h4'
      out.push(<Tag key={key++}>{bold(heading[2])}</Tag>)
      i++
      continue
    }

    // A table: a header row, a divider, then rows until the blank line.
    if (line.trim().startsWith('|') && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] ?? '')) {
      const head = cells(line)
      const body: string[][] = []
      i += 2
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        body.push(cells(lines[i]))
        i++
      }
      out.push(
        <table key={key++} className="answer__table">
          <thead>
            <tr>{head.map((c, n) => <th key={n} scope="col">{bold(c)}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, n) => (
              <tr key={n}>{row.map((c, m) => <td key={m}>{bold(c)}</td>)}</tr>
            ))}
          </tbody>
        </table>,
      )
      continue
    }

    // A list, taken as one block so the bullets sit in one <ul>.
    if (/^\s*[*-]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[*-]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[*-]\s+/, ''))
        i++
      }
      out.push(<ul key={key++}>{items.map((it, n) => <li key={n}>{bold(it)}</li>)}</ul>)
      continue
    }

    // A paragraph, which runs until a blank line or something that is not one.
    const para: string[] = []
    while (
      i < lines.length
      && lines[i].trim() !== ''
      && !/^#{2,3}\s/.test(lines[i])
      && !/^\s*[*-]\s+/.test(lines[i])
      && !lines[i].trim().startsWith('|')
    ) {
      para.push(lines[i])
      i++
    }
    out.push(<p key={key++}>{bold(para.join(' '))}</p>)
  }

  return out
}

const cells = (row: string): string[] =>
  row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())

/** `**like this**` becomes bold. Everything else stays the text it is. */
function bold(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter((p) => p !== '').map((part, n) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={n}>{part.slice(2, -2)}</strong>
      : <span key={n}>{part}</span>,
  )
}
